import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { PACKAGES, AGENCY_PACKAGES } from "@/lib/data";

export default function Packages() {
    return (
        <div className="bg-white" data-testid="packages-page">
            {/* Header */}
            <section className="bg-[#0A192F] pt-32 md:pt-40 pb-20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Campaign Packages</div>
                    <h1 className="font-display text-4xl md:text-6xl font-bold text-white max-w-3xl leading-tight">
                        Reach that scales with your <span className="text-gold-gradient">budget.</span>
                    </h1>
                    <p className="text-white/70 mt-5 max-w-2xl">
                        Five clear tiers. Verified views. Real influencers. Pick the package that fits today — scale up the moment you see the proof.
                    </p>
                </div>
            </section>

            {/* Pricing grid */}
            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                        {PACKAGES.map((p) => (
                            <div
                                key={p.id}
                                className={`relative rounded-2xl p-7 card-lift ${
                                    p.featured
                                        ? "bg-[#0A192F] text-white border-2 border-[#D4AF37] lg:scale-105 shadow-2xl shadow-[#D4AF37]/20"
                                        : "bg-white border border-slate-200"
                                }`}
                                data-testid={`packages-card-${p.id}`}
                            >
                                {p.featured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A192F] text-[10px] font-bold tracking-wider uppercase">
                                        Most Popular
                                    </div>
                                )}
                                <div className="text-xs uppercase tracking-wider opacity-60 mb-1">{p.tagline}</div>
                                <h3 className={`font-display text-xl font-bold mb-3 ${p.featured ? "text-white" : "text-[#0A192F]"}`}>{p.name}</h3>
                                <div className="flex items-baseline gap-1 mb-1">
                                    <span className={`font-display text-4xl font-bold ${p.featured ? "text-[#D4AF37]" : "text-[#0A192F]"}`}>${p.price}</span>
                                </div>
                                <div className={`text-sm font-semibold mb-1 ${p.featured ? "text-white/90" : "text-slate-700"}`}>{p.views} views</div>
                                <div className={`text-xs mb-5 ${p.featured ? "text-white/60" : "text-slate-500"}`}>{p.influencers}</div>
                                <ul className="space-y-2 mb-6">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-xs">
                                            <CheckCircle size={14} weight="fill" className={p.featured ? "text-[#D4AF37] mt-0.5" : "text-[#0A192F] mt-0.5"} />
                                            <span className={p.featured ? "text-white/85" : "text-slate-600"}>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/schedule"
                                    className={`block text-center w-full py-2.5 rounded-full font-semibold text-sm transition-all ${
                                        p.featured ? "bg-[#D4AF37] text-[#0A192F] hover:bg-[#e3c14a]" : "bg-[#0A192F] text-white hover:bg-[#112240]"
                                    }`}
                                    data-testid={`packages-cta-${p.id}`}
                                >
                                    Schedule Campaign
                                </Link>
                            </div>
                        ))}
                    </div>

                    {/* Agency Packages */}
                    <div className="mt-24" data-testid="agency-packages-section">
                        <div className="text-center max-w-2xl mx-auto mb-10">
                            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Agency Packages</div>
                            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0A192F]">Enterprise-scale reach.</h2>
                            <p className="text-slate-600 mt-3">Higher-volume tiers for agencies, brand teams, and campaigns that need serious distribution.</p>
                        </div>
                        <div className="overflow-x-auto -mx-4 md:mx-0">
                            <div className="min-w-[620px] px-4 md:px-0">
                                <table className="w-full text-sm rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                                    <thead className="bg-[#0A192F] text-white">
                                        <tr>
                                            <th className="py-4 px-4 md:px-6 text-left font-semibold whitespace-nowrap">Price</th>
                                            <th className="py-4 px-4 md:px-6 text-left font-semibold whitespace-nowrap">Posters / Influencers</th>
                                            <th className="py-4 px-4 md:px-6 text-left font-semibold whitespace-nowrap">Est. Views</th>
                                            <th className="py-4 px-4 md:px-6 text-right"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-slate-100">
                                        {AGENCY_PACKAGES.map((p, i) => (
                                            <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${i % 2 === 1 ? "bg-slate-50/50" : ""}`} data-testid={`packages-agency-row-${p.id}`}>
                                                <td className="py-4 px-4 md:px-6 font-display text-xl md:text-2xl font-bold text-[#D4AF37] whitespace-nowrap">${p.price.toLocaleString()}</td>
                                                <td className="py-4 px-4 md:px-6 text-slate-700 whitespace-nowrap">{p.influencers}</td>
                                                <td className="py-4 px-4 md:px-6 font-semibold text-[#0A192F] whitespace-nowrap">{p.views}</td>
                                                <td className="py-4 px-4 md:px-6 text-right whitespace-nowrap">
                                                    <Link to="/schedule" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A192F] hover:bg-[#112240] text-white font-semibold text-xs transition-all" data-testid={`packages-agency-cta-${p.id}`}>
                                                        Book <ArrowRight size={12} />
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="md:hidden text-center text-[10px] text-slate-400 mt-2 uppercase tracking-wider">← Swipe to see more →</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="bg-[#F8FAFC] py-20 md:py-28">
                <div className="max-w-6xl mx-auto px-6 md:px-8 grid md:grid-cols-2 gap-6">
                    <div className="bg-[#0A192F] rounded-3xl p-8 md:p-10 text-white card-lift">
                        <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Buy 2 · Get 1 Free</div>
                        <h3 className="font-display text-3xl font-bold mb-3">3× Viral Packages</h3>
                        <p className="text-white/70 mb-6">Up to 60,000 verified views — pay for two, we throw in a third.</p>
                        <div className="font-display text-4xl font-bold text-[#D4AF37]">$1,000</div>
                        <Link to="/schedule" className="mt-5 inline-flex items-center gap-2 text-[#D4AF37] font-semibold" data-testid="bundle-2x1-cta">Claim deal <ArrowRight size={14} /></Link>
                    </div>
                    <div className="bg-gradient-to-br from-[#D4AF37] to-[#b8951e] rounded-3xl p-8 md:p-10 text-[#0A192F] card-lift">
                        <div className="text-xs tracking-[0.2em] uppercase text-[#0A192F]/70 mb-3">Enterprise</div>
                        <h3 className="font-display text-3xl font-bold mb-3">10× Viral Packages</h3>
                        <p className="text-[#0A192F]/80 mb-6">Up to 200,000 verified views. Save $2,025.</p>
                        <div className="flex items-baseline gap-3">
                            <span className="font-display text-4xl font-bold">$2,975</span>
                            <span className="line-through opacity-50">$5,000</span>
                        </div>
                        <Link to="/schedule" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A192F] text-white font-semibold text-sm" data-testid="bundle-enterprise-cta">Talk to sales <ArrowRight size={14} /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
